import type { ComponentProps, ReactNode } from 'react'
import type {
  SalesStatisticsBreakdownRow,
  BreakdownChartFields,
  BreakdownDisplayFields,
} from './-helpers'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { EmptyState } from '@/components/patterns/empty-state'
import { Card, CardContent } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import { cn, formatCurrency, formatNumber } from '@/lib/utils'
import { formatPieSharePercent, renderPieShareLabel } from '../-helpers'
import {
  buildBreakdownChartRows,
  buildBreakdownDisplayRows,
  buildOrderCountChartRows,
} from './-helpers'
import { SectionCardHeader } from './-section-card-header'

type MixCardProps = {
  rows: SalesStatisticsBreakdownRow[]
  fullHeight?: boolean
  className?: string
  isGlobalReport?: boolean
  isOrderBased?: boolean
}
type DisplayRow = SalesStatisticsBreakdownRow & BreakdownDisplayFields
type ChartRow = SalesStatisticsBreakdownRow & BreakdownChartFields

export function FulfillmentMixCard({
  rows,
  fullHeight = false,
  className,
  isGlobalReport = false,
  isOrderBased = false,
}: MixCardProps) {
  // Delivery vs. pickup is mainly a workload question, so the florist view leads with orders.
  const chartRows = isOrderBased ? buildOrderCountChartRows(rows) : buildBreakdownChartRows(rows)
  const displayRows = buildBreakdownDisplayRows(rows, chartRows)
  const leadingRow = displayRows.find((row) => Number(row.sales_share ?? 0) > 0) ?? displayRows[0]
  const centerValue = leadingRow ? formatPieSharePercent(leadingRow.sales_share ?? 0) : '—'
  const centerLabel = leadingRow?.label ?? 'No mix'

  return (
    <Card
      className={cn(
        'min-w-0 overflow-hidden',
        fullHeight && 'xl:flex xl:h-full xl:flex-col',
        className
      )}
    >
      <SectionCardHeader
        title={isOrderBased ? 'By Delivery/Pickup' : 'Fulfillment Mix'}
        description={
          isGlobalReport
            ? 'How sales are split by fulfillment type'
            : isOrderBased
              ? undefined
              : 'Order payments by fulfillment type'
        }
      />
      <CardContent className={cn('space-y-4', fullHeight && 'xl:flex xl:flex-1 xl:flex-col')}>
        {rows.length ? (
          <div className="grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] xl:grid-cols-1 2xl:grid-cols-[160px_minmax(0,1fr)]">
            <BreakdownPieChart
              chartRows={chartRows}
              centerValue={centerValue}
              label={isOrderBased && leadingRow ? `${leadingRow.label} orders` : centerLabel}
              emptyLabel="No mix to chart"
              compact
            />
            <div className="space-y-3">
              {displayRows.map((row) => (
                <FulfillmentLegendRow key={row.label} row={row} leadWithOrders={isOrderBased} />
              ))}
            </div>
          </div>
        ) : (
          <Typography variant="supporting">No data in the selected range.</Typography>
        )}
      </CardContent>
    </Card>
  )
}

export function ChannelMixCard({
  rows,
  fullHeight = false,
  className,
  isGlobalReport = false,
  isOrderBased = false,
}: MixCardProps) {
  const chartRows = buildBreakdownChartRows(rows)
  const displayRows = buildBreakdownDisplayRows(rows, chartRows)
  const leadingRow = displayRows.find((row) => Number(row.sales_share ?? 0) > 0) ?? displayRows[0]
  const centerValue = leadingRow ? formatPieSharePercent(leadingRow.sales_share ?? 0) : '—'
  const centerLabel = leadingRow?.label ?? 'No mix'

  return (
    <Card
      className={cn(
        'min-w-0 overflow-hidden',
        fullHeight && 'xl:flex xl:h-full xl:flex-col',
        className
      )}
    >
      <SectionCardHeader
        title={isOrderBased ? 'By Channel' : 'Channel Mix'}
        description={
          isGlobalReport
            ? 'How sales are split by payment channel'
            : isOrderBased
              ? undefined
              : 'Order payments by source'
        }
      />
      <CardContent className={cn('space-y-4', fullHeight && 'xl:flex xl:flex-1 xl:flex-col')}>
        {rows.length ? (
          <div className="grid gap-4 md:grid-cols-[160px_minmax(0,1fr)] xl:grid-cols-1 2xl:grid-cols-[160px_minmax(0,1fr)]">
            <BreakdownPieChart
              chartRows={chartRows}
              centerValue={centerValue}
              label={centerLabel}
              emptyLabel="No mix to chart"
              compact
            />
            <div className="space-y-3">
              {displayRows.map((row) => (
                <ChannelSummaryTile key={row.label} row={row} />
              ))}
            </div>
          </div>
        ) : (
          <Typography variant="supporting">No data in the selected range.</Typography>
        )}
      </CardContent>
    </Card>
  )
}

// One figure leads and the other sits quietly underneath.
function MixAmount({
  row,
  leadWithOrders = false,
}: {
  row: SalesStatisticsBreakdownRow
  leadWithOrders?: boolean
}) {
  const orderCount = Number(row.order_count ?? 0)
  const orders = `${formatNumber(orderCount)} ${orderCount === 1 ? 'order' : 'orders'}`
  const sales = formatCurrency(row.total_sales)

  return (
    <div className="text-right">
      <Typography variant="labelStrong" className="block tabular-nums">
        {leadWithOrders ? orders : sales}
      </Typography>
      <Typography variant="caption" className="block tabular-nums">
        {leadWithOrders ? sales : orders}
      </Typography>
    </div>
  )
}

function FulfillmentLegendRow({
  row,
  leadWithOrders = false,
}: {
  row: DisplayRow
  leadWithOrders?: boolean
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="h-2.5 w-2.5 shrink-0 self-center rounded-full"
          style={{ backgroundColor: row.accentColor ?? 'var(--color-chart-1)' }}
          aria-hidden="true"
        />
        <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1">
          <span className="truncate text-sm text-foreground">{row.label}</span>
          <MixAmount row={row} leadWithOrders={leadWithOrders} />
        </div>
      </div>
    </div>
  )
}

function ChannelSummaryTile({ row }: { row: DisplayRow }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: row.accentColor ?? 'var(--color-chart-1)' }}
            aria-hidden="true"
          />
          <span className="truncate text-sm text-foreground">{row.label}</span>
        </div>
        <MixAmount row={row} />
      </div>
    </div>
  )
}

function BreakdownPieChart({
  chartRows,
  centerValue,
  label,
  emptyLabel = 'No mix to chart',
  compact = false,
}: {
  chartRows: ChartRow[]
  centerValue: ReactNode
  label: ReactNode
  emptyLabel?: string
  compact?: boolean
}) {
  if (!chartRows.length) {
    return <EmptyState size="compact" surface="transparent" title={emptyLabel} />
  }

  const chartConfig = Object.fromEntries(
    chartRows.map((row) => [
      row.chartKey,
      {
        label: row.label,
        color: row.fill,
      },
    ])
  )

  return (
    <div className="flex flex-col items-center justify-center">
      <ChartContainer
        config={chartConfig}
        className={cn(
          'aspect-square w-full',
          compact ? 'h-[150px] max-w-[150px]' : 'h-[240px] max-w-[280px]'
        )}
      >
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(_value, name) => {
                  const row = chartRows.find((candidate) => candidate.chartKey === name)
                  if (!row) return null
                  return (
                    <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                      <span>{row.label}</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatPieSharePercent(row.share)}
                      </span>
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatCurrency(row.total_sales)}
                      </span>
                      <span className="text-muted-foreground">Orders</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatNumber(Number(row.order_count ?? 0))}
                      </span>
                    </div>
                  )
                }}
              />
            }
          />
          <Pie
            data={chartRows}
            dataKey="chart_value"
            nameKey="chartKey"
            isAnimationActive={false}
            innerRadius={compact ? 42 : 68}
            outerRadius={compact ? 62 : 96}
            paddingAngle={2}
            strokeWidth={2}
            labelLine
            label={compact ? false : renderPieShareLabel}
          >
            <Label
              content={({ viewBox }) => (
                <BreakdownPieCenterLabel
                  viewBox={viewBox}
                  value={centerValue}
                  label={label}
                  compact={compact}
                />
              )}
            />
            {chartRows.map((row) => (
              // oxlint-disable-next-line typescript/no-deprecated -- grandfathered
              <Cell key={row.chartKey} fill={`var(--color-${row.chartKey})`} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
    </div>
  )
}

function BreakdownPieCenterLabel({
  viewBox,
  value,
  label,
  compact = false,
}: {
  viewBox: ComponentProps<typeof Label>['viewBox']
  value: ReactNode
  label: ReactNode
  compact?: boolean
}) {
  if (
    !viewBox ||
    !('cx' in viewBox) ||
    typeof viewBox.cx !== 'number' ||
    !('cy' in viewBox) ||
    typeof viewBox.cy !== 'number'
  ) {
    return null
  }

  return (
    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
      <tspan
        x={viewBox.cx}
        y={viewBox.cy - (compact ? 2 : 4)}
        className={cn(
          'fill-foreground font-semibold tabular-nums',
          compact ? 'text-sm' : 'text-base'
        )}
      >
        {value}
      </tspan>
      <tspan
        x={viewBox.cx}
        y={viewBox.cy + (compact ? 14 : 18)}
        className={cn('fill-muted-foreground', compact ? 'text-[10px]' : 'text-xs')}
      >
        {label}
      </tspan>
    </text>
  )
}
